import { useState, type FormEvent, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { OrgNav } from '../components/OrgNav.js';
import { Card, ErrorBanner, Loading, Spinner } from '../components/ui.js';
import { api, type Organization } from '../lib/api.js';
import { formatDate } from '../lib/format.js';
import { useAction, useAsync } from '../lib/hooks.js';

/**
 * Organization settings.
 *
 * Renaming is separated from the rest of the app because changing the slug
 * changes an identifier other people may have bookmarked or scripted against.
 * The consequence is stated on the form rather than discovered afterwards.
 */
export function SettingsPage(): ReactNode {
  const { orgId = '' } = useParams();

  const org = useAsync(() => api.get<Organization>(`/api/organizations/${orgId}`), [orgId]);
  const { run, pending, error } = useAction();

  /**
   * Edits, or null for "showing the server's values".
   *
   * The fields are derived from the loaded organization rather than seeded into
   * state by an effect: that avoids a cascading render, and clearing this back
   * to null after a save re-seeds them from the server — which matters because
   * the server normalises the slug and may not have stored what was typed.
   */
  const [edits, setEdits] = useState<{ name: string; slug: string } | null>(null);
  const [saved, setSaved] = useState(false);

  if (org.loading) return <Loading />;
  if (org.error || !org.data) {
    return (
      <div className="container">
        <ErrorBanner error={org.error} />
      </div>
    );
  }

  const current = org.data;
  const canManage = current.role === 'owner' || current.role === 'admin';
  const name = edits?.name ?? current.name;
  const slug = edits?.slug ?? current.slug;
  const dirty = name !== current.name || slug !== current.slug;

  const edit = (patch: { name?: string; slug?: string }): void => {
    setEdits({ name, slug, ...patch });
  };

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaved(false);
    await run(async () => {
      // Only what changed. Sending an unchanged slug would still be a slug
      // change as far as the server's uniqueness check is concerned.
      const patch: { name?: string; slug?: string } = {};
      if (name !== current.name) patch.name = name;
      if (slug !== current.slug) patch.slug = slug;
      await api.patch<Organization>(`/api/organizations/${orgId}`, patch);
      setSaved(true);
      setEdits(null);
      org.reload();
    });
  }

  return (
    <div className="container">
      <div className="page-head">
        <div>
          <h1>Settings</h1>
          <p>How this organization is named and addressed.</p>
        </div>
      </div>

      <OrgNav orgId={orgId} />

      <div className="stack">
        <ErrorBanner message={error} />
        {saved && !dirty && <div className="banner banner-success">Saved.</div>}

        <Card title="Name and address">
          <form onSubmit={(e) => void save(e)} className="stack">
            <div>
              <label htmlFor="org-name">Name</label>
              <input
                id="org-name"
                value={name}
                onChange={(e) => edit({ name: e.target.value })}
                maxLength={120}
                disabled={!canManage}
                required
              />
            </div>

            <div>
              <label htmlFor="org-slug">Slug</label>
              <input
                id="org-slug"
                value={slug}
                onChange={(e) => edit({ slug: e.target.value })}
                maxLength={48}
                disabled={!canManage}
                className="mono"
                required
              />
              <p className="field-hint">
                Lowercase letters, digits and hyphens. Anything else is folded into that form.
                Changing it will break links anyone has saved to the old one.
              </p>
            </div>

            {canManage ? (
              <div className="button-row">
                <button type="submit" className="button-primary" disabled={pending || !dirty}>
                  {pending && <Spinner />}
                  Save changes
                </button>
                {dirty && (
                  <button
                    type="button"
                    className="button-secondary"
                    disabled={pending}
                    onClick={() => setEdits(null)}
                  >
                    Discard
                  </button>
                )}
              </div>
            ) : (
              <p className="muted small">Only an owner or admin can change these.</p>
            )}
          </form>
        </Card>

        <Card title="Details">
          <table>
            <tbody>
              <tr>
                <td className="muted">Organization id</td>
                <td className="mono small">{current.id}</td>
              </tr>
              <tr>
                <td className="muted">Created</td>
                <td>{formatDate(current.createdAt)}</td>
              </tr>
              <tr>
                <td className="muted">Your role</td>
                <td>
                  <span className="badge">{current.role}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}
